import React, { useEffect, useMemo, useState } from 'react'
import {
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import Portlet from 'components/reusable/portlet'
import { toast } from 'react-toastify'
import { businessManagerService } from 'app/service/business-manager'
import {
  BusinessError,
  SystemUserAssetConnection,
  SystemUserAssetType,
  SystemUserBusinessConnection,
} from '@marketing-milk/interfaces'

const CONNECTIONS_PER_PAGE = 25

// short label shown per error, same wording as AuthorizersPage's BUSINESS_ERROR_LABELS
const BUSINESS_ERROR_LABELS: Record<BusinessError, string> = {
  [BusinessError.ad_account_access_error]: 'Ad account access error',
  [BusinessError.page_access_error]: 'Page access error',
  [BusinessError.lead_access_error]: 'Lead access error',
  [BusinessError.offline_upload_error]: 'Offline upload error',
  [BusinessError.google_ads_access_error]: 'Google Ads access error',
}

const ASSET_TYPE_LABELS: Record<SystemUserAssetType, string> = {
  adaccount: 'Ad Account',
  page: 'Page',
  dataset: 'Dataset',
  offline: 'Offline Dataset',
  instagram: 'Instagram',
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(`${label} copied to clipboard!`)
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}.`)
  }
}

// small inline copy affordance for an ID — used for system user IDs, BM IDs, and per-asset FB IDs so
// staff can grab the exact value to search Facebook's Business Manager / Graph API Explorer directly.
function CopyableId({ value, label }: { value: string; label: string }) {
  return (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        style={{ padding: 2 }}
        onClick={e => {
          e.stopPropagation()
          copyToClipboard(value, label)
        }}
      >
        <FileCopyOutlinedIcon style={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  )
}

function matchesSearch(connection: SystemUserBusinessConnection, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  if (connection.system_user_id.toLowerCase().includes(q)) return true
  if (connection.client_business_manager_id.toLowerCase().includes(q)) return true
  if (connection.fb_business_name?.toLowerCase().includes(q)) return true
  return connection.connections.some(c => c.business_name.toLowerCase().includes(q))
}

// one line per business (mirrors AuthorizersPage's toBusinessRows) — a business can have several
// assets on the same system user (ad account + page, say), each with its own monitored/error state,
// so they're combined onto a single row rather than one row per asset.
interface BusinessRow {
  business_id: number
  business_name: string
  assets: SystemUserAssetConnection[]
}

function toBusinessRows(connection: SystemUserBusinessConnection): BusinessRow[] {
  const map = new Map<number, BusinessRow>()
  for (const asset of connection.connections) {
    const existing = map.get(asset.business_id)
    if (existing) {
      existing.assets.push(asset)
    } else {
      map.set(asset.business_id, {
        business_id: asset.business_id,
        business_name: asset.business_name,
        assets: [asset],
      })
    }
  }
  return Array.from(map.values())
}

function SystemUserRow({
  connection,
  searchQuery,
  errorsOnly,
}: {
  connection: SystemUserBusinessConnection
  searchQuery: string
  errorsOnly: boolean
}) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [searchQuery, errorsOnly])

  const allRows = useMemo(() => toBusinessRows(connection), [connection])

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    let rows = allRows
    if (errorsOnly) rows = rows.filter(r => r.assets.some(a => (a.errors?.length ?? 0) > 0))
    if (!q) return rows
    // a query matching the system user id, BM id, or resolved BM name qualifies the whole group —
    // don't additionally filter its business rows by name in that case, or every business with a
    // non-matching name would vanish even though the group itself is what the search was looking for.
    const matchesGroupIdentity =
      connection.system_user_id.toLowerCase().includes(q) ||
      connection.client_business_manager_id.toLowerCase().includes(q) ||
      (connection.fb_business_name?.toLowerCase().includes(q) ?? false)
    if (matchesGroupIdentity) return rows
    return rows.filter(r => r.business_name.toLowerCase().includes(q))
  }, [
    allRows,
    connection.system_user_id,
    connection.client_business_manager_id,
    connection.fb_business_name,
    searchQuery,
    errorsOnly,
  ])

  const totalPages = Math.ceil(filteredRows.length / CONNECTIONS_PER_PAGE)
  const pageRows = filteredRows.slice(
    (page - 1) * CONNECTIONS_PER_PAGE,
    page * CONNECTIONS_PER_PAGE
  )
  const errorCount = filteredRows.filter(r =>
    r.assets.some(a => (a.errors?.length ?? 0) > 0)
  ).length
  const businessCount = allRows.length

  useEffect(() => {
    if (searchQuery.trim() && filteredRows.length > 0) setOpen(true)
  }, [searchQuery, filteredRows.length])

  if (filteredRows.length === 0 && (searchQuery.trim() || errorsOnly)) return null

  return (
    <div
      style={{ marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          background: open ? '#fafafa' : '#fff',
          userSelect: 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <IconButton
          size="small"
          style={{ padding: 2 }}
          onClick={e => {
            e.stopPropagation()
            setOpen(v => !v)
          }}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
        {connection.fb_business_picture_uri ? (
          <img
            src={connection.fb_business_picture_uri}
            alt={connection.fb_business_name ?? connection.client_business_manager_id}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              flexShrink: 0,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: '#F3E8FF',
              color: '#7C3AED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {connection.fb_business_name?.[0]?.toUpperCase() ?? 'S'}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, lineHeight: 1.2 }}>
              {connection.fb_business_name ?? 'Unknown Business'} (
              {connection.client_business_manager_id})
            </Typography>
            <CopyableId value={connection.client_business_manager_id} label="Business Manager ID" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
              System User {connection.system_user_id}
            </Typography>
            <CopyableId value={connection.system_user_id} label="System User ID" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Chip
            label={`${businessCount} business${businessCount !== 1 ? 'es' : ''}`}
            size="small"
          />
          {errorCount > 0 && (
            <Chip
              icon={<WarningIcon style={{ fontSize: 13 }} />}
              label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
              size="small"
              style={{ background: '#FEE2E2', color: '#B91C1C' }}
            />
          )}
        </div>
      </div>

      <Collapse in={open} unmountOnExit>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f5f5f5' }}>
                <TableCell>Business</TableCell>
                <TableCell>Assets</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={row.business_id}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Link
                        href={`/admin/business/beta/accounts/edit/${row.business_id}`}
                        variant="body2"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        {row.business_name}
                      </Link>
                      <CopyableId value={String(row.business_id)} label="Business ID" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {row.assets.map(asset => (
                        <div
                          key={asset.asset_type}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={ASSET_TYPE_LABELS[asset.asset_type]}
                            size="small"
                            style={{
                              height: 18,
                              fontSize: 11,
                              background: '#EEF4FF',
                              color: '#1877F2',
                              fontWeight: 600,
                            }}
                          />
                          {asset.asset_id && (
                            <>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontFamily: 'monospace' }}
                              >
                                {asset.asset_id}
                              </Typography>
                              <CopyableId
                                value={asset.asset_id}
                                label={`${ASSET_TYPE_LABELS[asset.asset_type]} ID`}
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <BusinessAssetsStatus assets={row.assets} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '6px 12px',
              borderTop: '1px solid #e0e0e0',
            }}
            onClick={e => e.stopPropagation()}
          >
            <Typography variant="caption" color="textSecondary">
              {(page - 1) * CONNECTIONS_PER_PAGE + 1}–
              {Math.min(page * CONNECTIONS_PER_PAGE, filteredRows.length)} of {filteredRows.length}
            </Typography>
            <Tooltip title="Previous">
              <span>
                <IconButton size="small" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </Collapse>
    </div>
  )
}

// combines every asset this business has on this system user into one status cell — a business can
// have multiple broken assets at once (e.g. Page AND Ad Account each with their own errors), so each
// asset's problems are listed with which asset they belong to, rather than only showing one status.
function BusinessAssetsStatus({ assets }: { assets: SystemUserAssetConnection[] }) {
  const monitored = assets.filter(a => a.monitored)
  const unmonitored = assets.filter(a => !a.monitored)
  const broken = monitored.filter(a => (a.errors?.length ?? 0) > 0)

  if (broken.length === 0 && unmonitored.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
        <Typography variant="caption" style={{ color: '#757575' }}>
          Connected
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {broken.map(asset =>
        (asset.errors ?? []).map(err => (
          <div
            key={`${asset.asset_type}-${err}`}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <WarningIcon style={{ fontSize: 15, color: '#f44336' }} />
            <Typography variant="caption" style={{ color: '#f44336' }}>
              {ASSET_TYPE_LABELS[asset.asset_type]}: {BUSINESS_ERROR_LABELS[err]}
            </Typography>
          </div>
        ))
      )}
      {monitored.length > broken.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
          <Typography variant="caption" style={{ color: '#757575' }}>
            Other assets connected
          </Typography>
        </div>
      )}
      {unmonitored.length > 0 && (
        <Tooltip title="No access-error detection exists for this asset type yet">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <HelpOutlineIcon style={{ fontSize: 15, color: '#9e9e9e' }} />
            <Typography variant="caption" style={{ color: '#9e9e9e' }}>
              {unmonitored.map(a => ASSET_TYPE_LABELS[a.asset_type]).join(', ')} not monitored
            </Typography>
          </div>
        </Tooltip>
      )}
    </div>
  )
}

function SystemUsersPageComponent() {
  const [data, setData] = useState<SystemUserBusinessConnection[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [errorsOnly, setErrorsOnly] = useState(false)

  useEffect(() => {
    businessManagerService
      .getSystemUserBusinesses()
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }, [])

  const visibleConnections = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase().trim()
    let list = data
    if (errorsOnly) {
      list = list.filter(c => c.connections.some(a => (a.errors?.length ?? 0) > 0))
    }
    if (q) list = list.filter(c => matchesSearch(c, q))
    return [...list].sort(
      (a, b) =>
        new Set(b.connections.map(c => c.business_id)).size -
        new Set(a.connections.map(c => c.business_id)).size
    )
  }, [data, search, errorsOnly])

  const totalBusinesses = useMemo(
    () => (data ? new Set(data.flatMap(c => c.connections.map(a => a.business_id))).size : 0),
    [data]
  )

  const totalErrors = useMemo(() => {
    if (!data) return 0
    // count distinct businesses with at least one broken asset, deduped by business_id across every
    // system user — the same business can appear under more than one system user (its assets can
    // span different BMs), so summing each group's own count independently would double-count it.
    const brokenBusinessIDs = new Set<number>()
    for (const c of data) {
      for (const asset of c.connections) {
        if ((asset.errors?.length ?? 0) > 0) brokenBusinessIDs.add(asset.business_id)
      }
    }
    return brokenBusinessIDs.size
  }, [data])

  return (
    <Portlet
      title="Facebook Builder System Users"
      headTools={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          {data && (
            <Typography variant="body2" color="textSecondary">
              {data.length} system user{data.length !== 1 ? 's' : ''} · {totalBusinesses} business
              {totalBusinesses !== 1 ? 'es' : ''}
              {totalErrors > 0 && (
                <>
                  {' · '}
                  <span style={{ color: '#B91C1C', fontWeight: 600 }}>
                    {totalErrors} error{totalErrors !== 1 ? 's' : ''}
                  </span>
                </>
              )}
            </Typography>
          )}
          <Chip
            icon={<WarningIcon style={{ fontSize: 14 }} />}
            label="Errors only"
            size="small"
            clickable
            onClick={() => setErrorsOnly(v => !v)}
            style={
              errorsOnly ? { background: '#FEE2E2', color: '#B91C1C', fontWeight: 600 } : undefined
            }
          />
          <TextField
            placeholder="Search system user, BM ID, or business..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            style={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <CircularProgress />
        </div>
      ) : !data || visibleConnections.length === 0 ? (
        <Typography color="textSecondary" style={{ padding: 16 }}>
          {search ? 'No results match your search.' : 'No Facebook Builder system users found.'}
        </Typography>
      ) : (
        <div>
          {visibleConnections.map(connection => (
            <SystemUserRow
              key={connection.system_user_id}
              connection={connection}
              searchQuery={search}
              errorsOnly={errorsOnly}
            />
          ))}
        </div>
      )}
    </Portlet>
  )
}

export const SystemUsersPage = SystemUsersPageComponent
